import { Helmet } from 'react-helmet';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Shield, MapPin, FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ConsumerHealthNotice = () => {
  return (
    <>
      <Helmet>
        <title>Consumer Health Data Notice | Therappi</title>
        <meta name="description" content="State-specific consumer health data privacy notices for Washington, Nevada, and other US states with health data privacy laws." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Shield className="h-8 w-8 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">Consumer Health Data Notice</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              State-specific notices about how we collect, use, and protect your consumer health data 
              in compliance with state privacy laws.
            </p>
            <p className="text-sm text-muted-foreground mt-4">
              Last updated: December 2024
            </p>
          </div>

          {/* Who this applies to */}
          <Alert className="mb-8">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle>Who This Notice Applies To</AlertTitle>
            <AlertDescription>
              This notice provides additional information for residents of US states with specific consumer 
              health data privacy laws. These rights are in addition to our standard Privacy Policy.
            </AlertDescription>
          </Alert>

          <Tabs defaultValue="washington" className="mb-8">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="washington" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Washington
              </TabsTrigger>
              <TabsTrigger value="nevada" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Nevada
              </TabsTrigger>
              <TabsTrigger value="connecticut" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Connecticut
              </TabsTrigger>
            </TabsList>

            {/* Washington State - My Health My Data Act */}
            <TabsContent value="washington">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Washington My Health My Data Act
                    </CardTitle>
                    <Badge variant="outline">RCW 19.373</Badge>
                  </div>
                  <CardDescription>
                    Effective March 31, 2024 (small businesses: June 30, 2024)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Consumer Health Data We Collect</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Under Washington law, "consumer health data" includes information that identifies a consumer's 
                      health status. We may collect:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Mental and psychological health information shared in therapy conversations</li>
                      <li>Sleep patterns and quality data</li>
                      <li>Mood and emotional state tracking</li>
                      <li>Nutrition and dietary information</li>
                      <li>Exercise and physical activity data (from connected wearables)</li>
                      <li>Reproductive health tracking (if you use those features)</li>
                    </ul>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Purpose of Collection</h3>
                    <p className="text-sm text-muted-foreground">
                      We collect consumer health data solely to provide our wellness services, including:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground mt-2">
                      <li>Delivering AI-powered therapeutic conversations</li>
                      <li>Tracking your wellness progress over time</li>
                      <li>Providing personalised insights and recommendations</li>
                      <li>Connecting with your healthcare providers (with your explicit consent)</li>
                    </ul>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Your Rights Under Washington Law</h3>
                    <div className="grid sm:grid-cols-2 gap-4 mt-3">
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">Right to Know</h4>
                        <p className="text-sm text-muted-foreground">
                          Request what consumer health data we have collected about you.
                        </p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">Right to Delete</h4>
                        <p className="text-sm text-muted-foreground">
                          Request deletion of your consumer health data.
                        </p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">Right to Withdraw Consent</h4>
                        <p className="text-sm text-muted-foreground">
                          Withdraw consent for collection or sharing at any time.
                        </p>
                      </div>
                      <div className="p-4 bg-muted/30 rounded-lg">
                        <h4 className="font-medium text-foreground mb-1">No Discrimination</h4>
                        <p className="text-sm text-muted-foreground">
                          Exercise these rights without discrimination.
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-semibold text-foreground mb-2">We Do NOT</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Sell your consumer health data</li>
                      <li>Share your data for advertising purposes</li>
                      <li>Use geofencing around healthcare facilities</li>
                      <li>Share data with third parties except as described in our Privacy Policy</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Nevada */}
            <TabsContent value="nevada">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Nevada Consumer Health Data Privacy Law
                    </CardTitle>
                    <Badge variant="outline">SB 370</Badge>
                  </div>
                  <CardDescription>
                    Effective January 1, 2025
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Your Rights Under Nevada Law</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Nevada residents have specific rights regarding consumer health data similar to Washington state:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Right to access your consumer health data</li>
                      <li>Right to request deletion of your data</li>
                      <li>Right to opt out of the sale of health data (we do not sell health data)</li>
                      <li>Right to withdraw consent for data processing</li>
                    </ul>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-semibold text-foreground mb-2">How to Exercise Your Rights</h3>
                    <p className="text-sm text-muted-foreground">
                      Nevada residents can submit requests through the same channels as Washington residents. 
                      We will respond to verified requests within 45 days.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Connecticut */}
            <TabsContent value="connecticut">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-primary" />
                      Connecticut Data Privacy Act (CTDPA)
                    </CardTitle>
                    <Badge variant="outline">Public Act 22-15</Badge>
                  </div>
                  <CardDescription>
                    Health data provisions effective July 1, 2023
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Sensitive Data Protections</h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      Connecticut law treats health-related data as "sensitive data" requiring opt-in consent. 
                      When you use Therappi, you provide this consent through our onboarding process.
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Your Rights</h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      <li>Right to access, correct, and delete personal data</li>
                      <li>Right to data portability</li>
                      <li>Right to opt out of targeted advertising (we do not target advertise)</li>
                      <li>Right to opt out of profiling for automated decisions</li>
                      <li>Right to appeal any denial of your request</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* How to exercise rights */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>How to Exercise Your Rights</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              <p className="mb-4">
                Regardless of your state, you can exercise your consumer health data rights by:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">Email Request</h4>
                  <p className="text-sm">
                    Send your request to{" "}
                    <a href="mailto:privacy@therappi.app" className="text-primary hover:underline">
                      privacy@therappi.app
                    </a>
                  </p>
                </div>
                <div className="p-4 border rounded-lg">
                  <h4 className="font-medium text-foreground mb-2">In-App Settings</h4>
                  <p className="text-sm">
                    Go to Settings → Privacy → Data Rights to submit requests directly
                  </p>
                </div>
              </div>
              <p className="text-sm mt-4">
                We will respond to verified requests within 45 days. For complex requests, we may extend 
                this by an additional 45 days with notice.
              </p>
            </CardContent>
          </Card>

          {/* Additional states notice */}
          <Card>
            <CardHeader>
              <CardTitle>Other State Laws</CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground">
              <p>
                We monitor developments in consumer health data privacy laws across all US states. 
                As new laws become effective, we will update this notice to include state-specific information.
              </p>
              <p className="mt-3">
                States with pending or recently enacted consumer health data laws include:
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge variant="secondary">California (CCPA/CPRA)</Badge>
                <Badge variant="secondary">Colorado</Badge>
                <Badge variant="secondary">Virginia</Badge>
                <Badge variant="secondary">Utah</Badge>
                <Badge variant="secondary">Oregon</Badge>
                <Badge variant="secondary">Texas</Badge>
                <Badge variant="secondary">Montana</Badge>
              </div>
              <p className="text-sm mt-4">
                Residents of these states may have additional rights under their state's general privacy laws. 
                Please see our main <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> for details.
              </p>
            </CardContent>
          </Card>

          {/* Back link */}
          <div className="mt-8 text-center">
            <Button variant="outline" asChild>
              <a href="/privacy">← Back to Privacy Policy</a>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsumerHealthNotice;
